import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Menu, X, Phone, Mail, ArrowRight,
  HelpCircle, Users, Briefcase, MessageSquare, Home as HomeIcon, Layers,
  Award, Target, Globe, Zap, Shield, CheckCircle2, Headphones, DollarSign,
  Clock, MapPin, Calendar, Tag, BookOpen, FileText
} from 'lucide-react';
import { MarketsDropdown, MobileMarkets } from './MarketsDropdown';
import { LOGO_URL, CONTACT_INFO, mainServices, subServices, aboutLinks, getPageTitle } from '../../data/serviceConfig';

const aboutContent = {
  'Who We Are': {
    tagline: 'Our Story & Values',
    items: [
      { icon: BookOpen, title: 'Our Story', desc: 'Years of delivering engineering CAD drafting services that fulfill all industry benchmarks.' },
      { icon: Target, title: 'Our Mission', desc: 'Cost-effective, reliable, and customized CAD drafting outsourcing solutions.' },
      { icon: Award, title: 'Excellence', desc: 'Top-quality CAD drafting services that exceed expectations.' },
      { icon: Shield, title: 'Precision', desc: 'Every draft undergoes rigorous quality checks for accuracy.' },
      { icon: Users, title: 'Client-Centric', desc: 'Your vision is our priority, with tailored solutions.' },
      { icon: Globe, title: 'Global Reach', desc: 'Serving architects, engineers, and surveyors across the USA and beyond.' },
    ]
  },
  'Our Team': {
    tagline: '55+ Skilled Professionals',
    items: [
      { icon: Briefcase, title: 'Leadership Team', desc: 'Project directors guiding every project to success.' },
      { icon: Layers, title: 'CAD Specialists', desc: 'Senior drafters with years of hands-on experience.' },
      { icon: Globe, title: 'Survey Experts', desc: 'Dedicated land survey professionals ensuring accuracy.' },
      { icon: Shield, title: 'QA Team', desc: 'Quality assurance specialists ensuring error-free deliverables.' },
      { icon: Award, title: '15K+ Projects', desc: 'Successfully completed projects across multiple industries.' },
      { icon: Clock, title: '10+ Years', desc: 'Combined industry experience powering every project.' },
    ]
  },
  'Why Choose Us': {
    tagline: 'What Sets Us Apart',
    items: [
      { icon: CheckCircle2, title: 'Accurate & On-Time', desc: 'Rigorous quality inspections throughout every draft process.' },
      { icon: Headphones, title: 'Top-Notch Support', desc: 'Faultless communication and client satisfaction throughout.' },
      { icon: Zap, title: 'Smart & Efficient', desc: 'Modern CAD technology with faster turnaround times.' },
      { icon: DollarSign, title: 'Cost-Effective', desc: 'Premier quality drafts at competitive outsourcing rates.' },
      { icon: Target, title: 'Tailored Expertise', desc: 'Services for architecture, civil engineering, and land surveying.' },
      { icon: Users, title: 'Client-Centric', desc: 'Solutions precisely fulfilling your project targets.' },
    ]
  },
  'Careers': {
    tagline: 'Join Our Growing Team',
    items: [
      { icon: Briefcase, title: 'Senior CAD Drafter', desc: 'Remote / Orlando, FL  -  Full-time' },
      { icon: MapPin, title: 'Land Survey Technician', desc: 'Orlando, FL  -  Full-time' },
      { icon: Layers, title: 'Civil CAD Designer', desc: 'Remote  -  Full-time' },
      { icon: Layers, title: 'BIM Coordinator', desc: 'Remote / Pune, India  -  Full-time' },
      { icon: Clock, title: 'Flexible Work', desc: 'Remote and hybrid work options available.' },
      { icon: Award, title: 'Growth Opportunities', desc: 'Continuous learning and career development programs.' },
    ]
  },
  'Blog & News': {
    tagline: 'Latest Insights & Updates',
    items: [
      { icon: FileText, title: 'Future of CAD Drafting', desc: 'How AI and automation are transforming the industry.' },
      { icon: FileText, title: 'Land Survey Best Practices', desc: 'Tips for achieving accuracy in survey CAD drafting.' },
      { icon: FileText, title: 'Why Outsource CAD?', desc: 'Benefits of outsourcing to improve business efficiency.' },
      { icon: FileText, title: 'BIM in Construction', desc: 'How Building Information Modeling revolutionizes projects.' },
      { icon: FileText, title: '3D Visualization', desc: 'Transforming architectural design communication.' },
      { icon: Calendar, title: 'Industry Standards', desc: 'Civil CAD drafting compliance and regulations.' },
    ]
  },
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [hoveredAbout, setHoveredAbout] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();
  const servicesTimeoutRef = useRef(null);
  const aboutTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setAboutOpen(false);
    setHoveredService(null);
    document.title = getPageTitle(location.pathname);
  }, [location]);

  const handleServicesEnter = () => {
    clearTimeout(servicesTimeoutRef.current);
    setServicesOpen(true);
    setAboutOpen(false);
  };
  const handleServicesLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
      setHoveredService(null);
    }, 150);
  };
  const handleAboutEnter = () => {
    clearTimeout(aboutTimeoutRef.current);
    setAboutOpen(true);
    setServicesOpen(false);
  };
  const handleAboutLeave = () => {
    aboutTimeoutRef.current = setTimeout(() => setAboutOpen(false), 150);
  };

  const navItems = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'Services', href: '#', icon: Layers, isDropdown: true, dropdownType: 'services' },
    { name: 'Why Us?', href: '/about/why-us', icon: HelpCircle },
    { name: 'About Us', href: '#', icon: Users, isDropdown: true, dropdownType: 'about' },
    { name: 'Careers', href: '/about/careers', icon: Briefcase },
    { name: 'Contact Us', href: '/contact', icon: MessageSquare },
  ];

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-[#0c2d1f] text-white py-2.5 text-sm font-bold relative z-50">
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={CONTACT_INFO.phoneLink} className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="top-phone">
              <Phone size={14} />
              <span>{CONTACT_INFO.phone}</span>
            </a>
            <a href={CONTACT_INFO.emailLink} className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="top-email">
              <Mail size={14} />
              <span>{CONTACT_INFO.email}</span>
            </a>
            <a href={CONTACT_INFO.emailGenericLink} className="hidden md:flex items-center gap-2 hover:text-primary transition-colors" data-testid="top-email-generic">
              <Mail size={14} />
              <span>{CONTACT_INFO.emailGeneric}</span>
            </a>
          </div>
          <span className="hidden md:block topbar-shimmer font-bold">Professional CAD Drafting Services | 24/7 Support</span>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'shadow-xl' : ''} bg-white border-b border-border`} data-testid="main-navigation">
        <nav className="container-custom">
          <div className="flex items-center justify-between h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
              <img src={LOGO_URL} alt="Land Drafters LLC" className="h-[108px] w-auto"  width="360" height="120" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1 flex-nowrap">
              {navItems.map((item) => {
                if (item.dropdownType === 'services') {
                  return (
                    <div key={item.name} className="relative" onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave}>
                      <Link to="/services" className="nav-link-hover whitespace-nowrap flex items-center gap-1.5 text-foreground/80 hover:text-primary hover:font-black text-sm font-semibold uppercase tracking-wider px-3 py-3 transition-all duration-200" data-testid="nav-services-dropdown">
                        <Layers size={16} />
                        Services
                        <ChevronDown size={16} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                      </Link>
                      <AnimatePresence>
                        {servicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.15 }}
                            className="fixed left-0 right-0 mt-0 bg-white shadow-2xl border-t border-b border-border"
                            style={{ top: 'auto' }}
                          >
                            <div className="flex w-full">
                              {/* Left: Main Services */}
                              <div className="w-[320px] border-r border-border bg-[#f8faf8] shrink-0">
                                <Link to="/services" className="block bg-primary text-white px-4 py-2.5 hover:bg-primary/90 transition-colors" data-testid="nav-services-all">
                                  <h3 className="font-bold text-xs uppercase tracking-widest flex items-center justify-between">Our Services <ArrowRight size={12} /></h3>
                                </Link>
                                <div className="py-1 max-h-[60vh] overflow-y-auto">
                                  {mainServices.map((s) => {
                                    const SIcon = s.icon;
                                    const hasSubs = subServices[s.key]?.length > 0;
                                    return (
                                      <div key={s.key} onMouseEnter={() => hasSubs && setHoveredService(s.key)} className="relative">
                                        <Link
                                          to={s.href}
                                          className={`sub-link-hover flex items-center gap-3 px-4 py-2.5 text-sm transition-colors group ${hoveredService === s.key ? 'bg-primary/10 text-primary is-active' : 'hover:bg-primary/5'}`}
                                          data-testid={`nav-service-${s.key}`}
                                        >
                                          <SIcon size={18} className={`shrink-0 ${hoveredService === s.key ? 'text-primary' : 'text-primary/70 group-hover:text-primary'}`} />
                                          <span className="font-medium">{s.shortTitle}</span>
                                          {hasSubs && <ChevronDown size={12} className="ml-auto -rotate-90 text-muted-foreground" />}
                                        </Link>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Right: Sub-services */}
                              <div className="flex-1">
                                {hoveredService && subServices[hoveredService] ? (
                                  <div>
                                    <div className="bg-secondary text-white px-4 py-2.5">
                                      <h3 className="font-bold text-xs uppercase tracking-widest">
                                        {mainServices.find(s => s.key === hoveredService)?.shortTitle} Services
                                      </h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-0 max-h-[55vh] overflow-y-auto">
                                      {subServices[hoveredService].map((sub) => {
                                        const SubIcon = sub.icon;
                                        return (
                                          <Link key={sub.key} to={sub.href} className="sub-link-hover flex gap-3 px-4 py-3 hover:bg-secondary/5 transition-colors group border-b border-r border-border/40" data-testid={`nav-sub-${sub.key}`}>
                                            <div className="w-8 h-8 rounded bg-secondary/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-secondary/20 transition-colors">
                                              <SubIcon size={16} className="text-secondary/70 group-hover:text-secondary" />
                                            </div>
                                            <div className="min-w-0">
                                              <span className="text-sm font-semibold text-foreground group-hover:text-secondary block leading-tight">{sub.title}</span>
                                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{sub.description}</p>
                                              <span className="text-[10px] font-bold text-primary group-hover:text-secondary uppercase tracking-wider mt-1 inline-flex items-center gap-0.5">
                                                Learn more <ArrowRight size={9} />
                                              </span>
                                            </div>
                                          </Link>
                                        );
                                      })}
                                    </div>
                                    <div className="border-t border-border px-4 py-2.5">
                                      <Link to={mainServices.find(s => s.key === hoveredService)?.href || '#'} className="text-xs font-bold text-primary hover:text-secondary uppercase tracking-widest flex items-center gap-1">
                                        View All Services <ArrowRight size={12} />
                                      </Link>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-full p-8">
                                    <img src={LOGO_URL} alt="Land Drafters LLC" className="h-40 w-auto opacity-20 mb-4"  width="360" height="120" loading="lazy" />
                                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-4">Hover over a service to explore</p>
                                    <Link to="/services" className="text-xs font-bold text-primary hover:text-secondary uppercase tracking-widest flex items-center gap-1" data-testid="nav-services-all-cta">
                                      View All Services <ArrowRight size={12} />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Bottom Bar */}
                            <div className="bg-[#F5F5F5] border-t border-border">
                              <div className="px-6 py-2.5 flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Need a custom solution?</span>
                                <Link to="/get-quote" className="text-xs font-bold text-primary hover:text-secondary flex items-center gap-1">
                                  Get a Quote <ArrowRight size={12} />
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                if (item.dropdownType === 'about') {
                  return (
                    <div key={item.name} className="relative" onMouseEnter={handleAboutEnter} onMouseLeave={handleAboutLeave}>
                      <Link to="/about-us" className="nav-link-hover whitespace-nowrap flex items-center gap-1.5 text-foreground/80 hover:text-primary hover:font-black text-sm font-semibold uppercase tracking-wider px-3 py-3 transition-all duration-200" data-testid="nav-about-dropdown">
                        {item.icon && <item.icon size={16} />}
                        About Us
                        <ChevronDown size={16} className={`transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`} />
                      </Link>
                      <AnimatePresence>
                        {aboutOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.15 }}
                            className="fixed left-0 right-0 mt-0 bg-white shadow-2xl border-t border-b border-border"
                            style={{ top: 'auto' }}
                          >
                            <div className="flex w-full">
                              {/* Left: About Links */}
                              <div className="w-[320px] border-r border-border bg-[#f8faf8] shrink-0">
                                <Link to="/about-us" className="block bg-primary text-white px-4 py-2.5 hover:bg-primary/90 transition-colors" data-testid="nav-about-all">
                                  <h3 className="font-bold text-xs uppercase tracking-widest flex items-center justify-between">About Us <ArrowRight size={12} /></h3>
                                </Link>
                                <div className="py-1">
                                  {aboutLinks.map((link) => (
                                    <Link
                                      key={link.href}
                                      to={link.href}
                                      className={`sub-link-hover flex items-center gap-3 px-4 py-3 text-sm transition-colors group ${hoveredAbout === link.name ? 'bg-primary/10 border-l-2 border-primary is-active' : 'hover:bg-primary/5 border-l-2 border-transparent'}`}
                                      data-testid={`nav-about-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                      onMouseEnter={() => setHoveredAbout(link.name)}
                                    >
                                      <ArrowRight size={14} className={`shrink-0 ${hoveredAbout === link.name ? 'text-primary' : 'text-primary/70 group-hover:text-primary'}`} />
                                      <div>
                                        <span className={`font-medium block transition-colors ${hoveredAbout === link.name ? 'text-primary' : 'group-hover:text-primary'}`}>{link.name}</span>
                                        <span className="text-xs text-muted-foreground">{link.description}</span>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>

                              {/* Right: Content area */}
                              <div className="flex-1 p-6">
                                {hoveredAbout && aboutContent[hoveredAbout] ? (
                                  <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">{aboutContent[hoveredAbout].tagline}</p>
                                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                                      {aboutContent[hoveredAbout].items.map((item, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 rounded hover:bg-primary/5 transition-colors">
                                          <div className="w-8 h-8 bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                            <item.icon size={15} className="text-primary" />
                                          </div>
                                          <div>
                                            <span className="font-semibold text-sm block leading-tight">{item.title}</span>
                                            <span className="text-xs text-muted-foreground leading-snug">{item.desc}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-full">
                                    <img src={LOGO_URL} alt="Land Drafters LLC" className="h-32 w-auto opacity-15 mb-4"  width="360" height="120" loading="lazy" />
                                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-4">Hover over a link to explore</p>
                                    <Link to="/about-us" className="text-xs font-bold text-primary hover:text-secondary uppercase tracking-widest flex items-center gap-1" data-testid="nav-about-all-cta">
                                      View About Us <ArrowRight size={12} />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Bottom Bar */}
                            <div className="bg-[#F5F5F5] border-t border-border">
                              <div className="px-6 py-2.5 flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Want to work with us?</span>
                                <Link to="/contact" className="text-xs font-bold text-primary hover:text-secondary flex items-center gap-1">
                                  Contact Us <ArrowRight size={12} />
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="nav-link-hover whitespace-nowrap flex items-center gap-1.5 text-foreground/80 hover:text-primary hover:font-black text-sm font-semibold uppercase tracking-wider px-3 py-3 transition-all duration-200"
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.icon && <item.icon size={16} />}
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex flex-col items-stretch shrink-0">
              <MarketsDropdown compact />
              <Link to="/get-started" className="whitespace-nowrap bg-primary hover:bg-primary/90 text-white text-sm font-bold uppercase tracking-wider px-5 py-3.5 transition-colors" data-testid="nav-get-started">
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button className="lg:hidden p-2 text-foreground ml-auto" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white border-t border-border overflow-hidden max-h-[80vh] overflow-y-auto">
              <div className="px-4 md:px-8 py-4 space-y-2">
                <Link to="/" className="block py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">Home</Link>
                <div>
                  <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="flex items-center justify-between w-full py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">
                    Services <ChevronDown size={16} className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pl-4 space-y-1 mt-1">
                      <Link to="/services" className="flex items-center gap-2 py-2 text-sm font-bold text-primary hover:text-secondary uppercase tracking-wider" data-testid="mobile-nav-services-all">
                        View All Services <ArrowRight size={12} />
                      </Link>
                      {mainServices.map((s) => {
                        const SIcon = s.icon;
                        return (
                          <Link key={s.key} to={s.href} className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-primary">
                            <SIcon size={14} /> {s.shortTitle}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
                <MobileMarkets />
                <Link to="/about/why-us" className="block py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">Why Us?</Link>
                <div>
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-between w-full py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">
                    About Us <ChevronDown size={16} className={`transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileAboutOpen && (
                    <div className="pl-4 space-y-1 mt-1">
                      <Link to="/about-us" className="flex items-center gap-2 py-2 text-sm font-bold text-primary hover:text-secondary uppercase tracking-wider" data-testid="mobile-nav-about-all">
                        View About Us <ArrowRight size={12} />
                      </Link>
                      {aboutLinks.map((link) => (
                        <Link key={link.href} to={link.href} className="block py-2 text-sm text-muted-foreground hover:text-primary">{link.name}</Link>
                      ))}
                    </div>
                  )}
                </div>
                <Link to="/about/careers" className="block py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">Careers</Link>
                <Link to="/contact" className="block py-2.5 text-foreground font-bold uppercase tracking-widest text-sm">Contact Us</Link>
                <div className="flex flex-col gap-3 pt-4 border-t border-border">
                  <Link to="/get-started" className="bg-primary text-white text-center text-xs font-bold uppercase tracking-wider py-3">Get Started</Link>
                  <Link to="/get-quote" className="bg-secondary text-white text-center text-xs font-bold uppercase tracking-wider py-3">Get a Quote</Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navigation;
